/**
 * Juice moments: the effect recipe for each gameplay moment, as pure functions of an engine and
 * a little geometry (canvas px). game-juice.js resolves the geometry from the live game; the dev
 * gallery (render/toon/gallery/juice.js) calls the same recipes with fixed geometry, so what is
 * reviewed there is exactly what plays in the game.
 *
 * Each returns the screen shake it asks for: { add } (trauma, stacks) and/or { min } (raise
 * trauma to), and/or { kick: { px, angle } } (a directional punch; px at layout unit 1).
 */
import { tierPaletteKey, TIER_COLORS } from "./juice-palettes.js";

/** Trauma per moment (0–1). shake = trauma², so 0.3 is a nudge and 0.9 a real jolt. */
export var JUICE_TRAUMA = {
  boulderImpact: 0.1,
  boulderHit: 0.72,
  flaskBreak: 1,
  shieldBlock: 0.46,
  boulderBurst: 0.22,
  mergeBase: 0.16,
  mergePerTier: 0.036,
  dragonMade: 0.72,
  dragonCapped: 0.85,
  landingBase: 0.09,
  landingPerTier: 0.016,
  shakeBoosterPerMult: 0.15,
  clear: 0.26,
};

/**
 * Kicks (px at layout unit 1): merges and landings are too small for trauma (0.2–0.4 trauma is
 * a fraction of a pixel and gone before the wobble builds), so they punch the view down instead:
 * the new, heavier creature thumps into the jar. Trauma stays for the big moments.
 */
export var JUICE_KICK = {
  mergeBase: 2.4,
  mergePerTier: 0.42,
  landingBase: 1.1,
  landingPerTier: 0.2,
  // Mostly down, a little to either side so repeated kicks do not read as one axis.
  sideJitter: 0.35,
};

function downKick(px) {
  return { px: px, angle: Math.PI / 2 + (Math.random() * 2 - 1) * JUICE_KICK.sideJitter };
}

var GLASS_SPARKS = ["#ffffff", "#bdeeff", "#e2f7ff"];

function rand(a, b) {
  return a + Math.random() * (b - a);
}

function tierEntry(tier) {
  var i = Math.max(0, Math.min(TIER_COLORS.length - 1, Math.floor(Number(tier) || 0)));
  return TIER_COLORS[i];
}

/**
 * A boulder knocks the flask (HP lost, flask holds): a comic POW where it hit, glass glitter,
 * a few slivers and rock dust.
 * @param {object} J juice engine
 * @param {{ x: number, y: number, flaskW: number, rockR: number, unit?: number }} g contact point
 */
export function boulderHitFx(J, g) {
  var U = g.unit || 1;
  var w = g.flaskW;
  var br = g.rockR;
  J.pulse("flask", 1);
  J.flash(g.x, g.y, { palette: "impact", size: w * 0.38, life: 0.22 });
  J.ring(g.x, g.y, { radius: w * 0.46, palette: "impact", life: 0.28, width: 1.2 });
  J.sparkles(g.x, g.y, {
    count: 11, colors: GLASS_SPARKS, shape: "mix", speed: [100, 250], size: [2.6, 5.2], up: 80, gravity: 360,
  });
  J.shards(g.x, g.y, { count: 6, speed: 0.85 * U, size: 1.15 * U, life: [0.6, 0.95] });
  J.chunks(g.x, g.y, { material: "glass", count: 3, speed: 0.7 * U, size: 0.7 * U, life: [0.6, 0.9], gravity: 1000 });
  for (var i = 0; i < 3; i++) {
    J.puff(g.x + rand(-br, br) * 0.6, g.y + rand(-br, br) * 0.4, {
      tone: "stone", size: br * rand(0.45, 0.7), vx: rand(-40, 40), vy: rand(-50, -15), life: rand(0.4, 0.55), delay: i * 0.03,
    });
  }
  return { add: JUICE_TRAUMA.boulderHit };
}

/**
 * The flask breaks: a big glass blast in its middle, a POW at the hit, glass chunks, slivers
 * (glass confetti) and glitter, a wide ring. Plays over the flask's own shard burst art.
 * @param {{ x: number, y: number, cx: number, cy: number, flaskW: number, unit?: number }} g
 *   (x, y) the hit, (cx, cy) the flask middle
 */
export function flaskBreakFx(J, g) {
  var U = g.unit || 1;
  var w = g.flaskW;
  J.pulse("flask", 1.4);
  J.flash(g.x, g.y, { palette: "impact", size: w * 0.62, life: 0.24 });
  J.blast(g.cx, g.cy, {
    palette: "glass", size: w * 1.55, reach: w * 0.95, fire: 1.2, smoke: 0.6,
    sparks: 1.5, chunks: "glass", chunkCount: 1.4, glow: 0.9, flash: 0.9, gravity: 420,
  });
  J.shards(g.cx, g.cy, { count: 18, speed: 1.15 * U, size: 1.5 * U, width: w * 0.6 });
  J.sparkles(g.cx, g.cy, {
    count: 16, colors: GLASS_SPARKS, shape: "mix", speed: [140, 320], size: [3, 6], life: [0.5, 0.9],
    up: 120, radius: w * 0.3, gravity: 380,
  });
  J.ring(g.cx, g.cy, { radius: w * 1.25, palette: "impact", life: 0.42, width: 1.6, delay: 0.04 });
  return { min: JUICE_TRAUMA.flaskBreak };
}

/**
 * The shield eats a boulder: gold rings running out from the bubble, a gold POW at the top,
 * a soft gold glow, sparkles from the bubble's edge.
 * @param {{ cx: number, cy: number, R: number, topY: number, flaskW: number }} g shield centre/radius
 */
export function shieldBlockFx(J, g) {
  J.glow(g.cx, g.cy, { size: g.R * 2.6, color: "#ffe08a", life: 0.32, alpha: 0.55 });
  J.ring(g.cx, g.cy, { radius: g.R * 1.18, palette: "gold", life: 0.42, width: 1.7, start: 0.75 });
  J.ring(g.cx, g.cy, { radius: g.R * 1.5, palette: "gold", life: 0.5, width: 1, start: 0.6, delay: 0.06 });
  J.flash(g.cx, g.topY, { palette: "gold", size: g.flaskW * 0.34 });
  J.sparkles(g.cx, g.cy, {
    count: 16, palette: "gold", shape: "mix", speed: [120, 260], size: [3, 5.5], radius: g.R * 0.9, up: 30, gravity: 200,
  });
  return { add: JUICE_TRAUMA.shieldBlock };
}

/**
 * A boulder bursts (on the shield or the flask mouth): stone blast with rock chunks; on the
 * mouth (`rimY` given) dust is also kicked out along the rim.
 * @param {{ x: number, y: number, r: number, rimY?: number }} g
 */
export function boulderBurstFx(J, g) {
  J.blast(g.x, g.y, {
    palette: "stone", size: g.r * 3.6, fire: 0.9, smoke: 0.7, sparks: 0.5, chunks: "rock",
    chunkCount: 1.1, flash: 0.55, gravity: 500,
  });
  if (g.rimY != null) J.thump(g.x, g.rimY, { reach: g.r * 2.1, palette: "stone", chunks: null, puffs: 7 });
  return { add: JUICE_TRAUMA.boulderBurst };
}

/**
 * A creature lands in the flask mouth: a tier-coloured pop (small starburst, a ring lying on
 * the mouth, sparkles fountaining up).
 * @param {{ x: number, y: number, flaskW: number, tier: number }} g
 */
export function catchFx(J, g) {
  var key = tierPaletteKey(g.tier);
  var col = tierEntry(g.tier);
  var w = g.flaskW;
  J.flash(g.x, g.y - w * 0.07, { palette: key, size: w * 0.27, life: 0.2 });
  J.ring(g.x, g.y, { radius: w * 0.4, palette: key, life: 0.32, squash: 0.42, width: 1.2 });
  J.sparkles(g.x, g.y - w * 0.04, {
    count: 12, colors: [col.accent, col.base, "#ffffff"], shape: "mix", speed: [110, 240],
    size: [3, 5.6], life: [0.4, 0.75], up: 130, angle: -Math.PI / 2, spread: Math.PI * 1.2, gravity: 360,
  });
  return null;
}

/** A gem goes in: pink pop at the mouth. @param {{ x: number, y: number, flaskW: number }} g */
export function gemFx(J, g) {
  var w = g.flaskW;
  J.flash(g.x, g.y - w * 0.06, { palette: "gem", size: w * 0.26, life: 0.2 });
  J.ring(g.x, g.y, { radius: w * 0.38, palette: "gem", life: 0.3, squash: 0.42, width: 1.2 });
  J.sparkles(g.x, g.y, {
    count: 12, colors: ["#ffffff", "#ff9cf5", "#ff6fd8"], shape: "mix", speed: [110, 240],
    size: [3, 5.6], life: [0.4, 0.75], up: 140, angle: -Math.PI / 2, spread: Math.PI * 1.2, gravity: 340,
  });
  return null;
}

/** Shield picked up: a gold ring blooms round the flask. @param {{ cx: number, cy: number, R: number }} g */
export function shieldPickupFx(J, g) {
  J.glow(g.cx, g.cy, { size: g.R * 2.4, color: "#ffe08a", life: 0.4, alpha: 0.5 });
  J.ring(g.cx, g.cy, { radius: g.R * 1.1, palette: "gold", life: 0.45, width: 1.4, start: 0.2 });
  J.sparkles(g.cx, g.cy, { count: 12, palette: "gold", shape: "mix", speed: [70, 180], radius: g.R * 0.7, up: 40, gravity: 120 });
  return null;
}

/** Revive (Fall): gold glow and ring, hearts and sparkles. @param {{ cx: number, cy: number, w: number, h: number }} g */
export function reviveFx(J, g) {
  J.glow(g.cx, g.cy, { size: g.h * 2, color: "#ffe08a", life: 0.5, alpha: 0.6 });
  J.ring(g.cx, g.cy, { radius: g.h * 0.9, palette: "gold", life: 0.5, width: 1.5, start: 0.1 });
  J.hearts(g.cx, g.cy - g.h * 0.2, { count: 8, radius: g.w * 0.35, size: [6, 10] });
  J.sparkles(g.cx, g.cy, { count: 14, palette: "gold", shape: "mix", speed: [80, 220], radius: g.w * 0.4, up: 60, gravity: 160 });
  return null;
}

/**
 * Two creatures merge into tier `tier`: a Ghost Circuit cartoon explosion in the new tier's
 * colours, growing with the tier; the Dragon gets a gold blast on top. Bodies grow a lot from
 * tier to tier (radius ×5 from Cell to Dragon), so the blast tracks the body's size with a
 * floor rather than a fixed multiple: small merges still pop, big ones do not swallow the jar.
 *
 * The blast reveals the new creature rather than covering it (`reveal: R`): the starburst, glow
 * and smoke sit behind it, the puffs, sparks and ring burst out from its rim, so its pop-in
 * (game-juice getMergePop) lands on top of the explosion as the payoff.
 * @param {{ x: number, y: number, R: number, tier: number, maxTier?: number, unit?: number }} g
 *   R = new body radius, unit = layout unit (~1 at a 150 px flask)
 */
export function mergeFx(J, g) {
  var t = Math.max(0, Math.floor(Number(g.tier) || 0));
  var R = g.R;
  var U = g.unit || 1;
  J.blast(g.x, g.y, {
    palette: tierPaletteKey(t), size: R * 2.3 + 46 * U, reach: R * 1.35 + 22 * U,
    fire: 1 + t * 0.03, smoke: 0.45, sparks: 0.9 + t * 0.1, glow: 0.45 + t * 0.03,
    stars: t >= 8 ? 0.25 : 0, gravity: 300, reveal: R,
  });
  var kick = downKick(JUICE_KICK.mergeBase + JUICE_KICK.mergePerTier * t);
  var top = g.maxTier != null ? g.maxTier : TIER_COLORS.length - 1;
  if (t >= top) {
    J.blast(g.x, g.y, {
      palette: "gold", size: R * 2.7 + 60 * U, reach: R * 2.2 + 30 * U, fire: 1.2, smoke: 0.6, sparks: 2, stars: 0.45,
      chunks: "gold", glow: 1, slow: 1.2, flash: 0.9, delay: 0.09, reveal: R,
    });
    J.ring(g.x, g.y, { radius: R * 2.9 + 40 * U, palette: "gold", life: 0.55, width: 1.8, delay: 0.16, start: 0.4 });
    return { add: JUICE_TRAUMA.mergeBase + JUICE_TRAUMA.mergePerTier * t, min: JUICE_TRAUMA.dragonMade, kick: kick };
  }
  return { add: JUICE_TRAUMA.mergeBase + JUICE_TRAUMA.mergePerTier * t, kick: kick };
}

/** Dragon + Dragon (capped): the biggest blast in the game. @param {{ x: number, y: number, R: number, unit?: number }} g */
export function dragonCappedFx(J, g) {
  var R = g.R;
  var U = g.unit || 1;
  J.blast(g.x, g.y, {
    palette: "gold", size: R * 3.1 + 70 * U, reach: R * 2.4 + 30 * U, fire: 1.4, smoke: 1, sparks: 2.4, stars: 0.4,
    chunks: "gold", chunkCount: 1.4, glow: 1, slow: 1.25, gravity: 320,
  });
  J.blast(g.x, g.y, { palette: tierPaletteKey(TIER_COLORS.length - 1), size: R * 2.2 + 40 * U, fire: 1, smoke: 0, sparks: 1, flash: 0.7, slow: 1.1 });
  J.ring(g.x, g.y, { radius: R * 3.2 + 40 * U, palette: "gold", life: 0.6, width: 2, delay: 0.08 });
  return { min: JUICE_TRAUMA.dragonCapped };
}

/** A dropped creature lands in the jar: a water thump under it. @param {{ x: number, y: number, r: number, tier: number, unit?: number }} g y = its bottom */
export function landingFx(J, g) {
  // Reach tracks the body with a floor, so a Cell's splash still shows and a T-rex's stays a splash.
  var U = g.unit || 1;
  J.thump(g.x, g.y, { reach: g.r * 1.1 + 16 * U, palette: "water", puffs: 6, puffScale: 1.6, chunks: null });
  J.sparkles(g.x, g.y - g.r * 0.15, {
    count: 7, colors: ["#ffffff", "#bfeaff"], shape: "dot", speed: [90, 190], size: [2.6, 4.4],
    up: 120, angle: -Math.PI / 2, spread: Math.PI * 0.9, gravity: 520,
  });
  J.sparkles(g.x, g.y - g.r * 0.3, {
    count: 3, shape: "bubble", speed: [20, 60], size: [3, 5], life: [0.6, 0.9],
    up: 70, angle: -Math.PI / 2, spread: Math.PI * 0.8, gravity: -90, drag: 1.5, radius: g.r * 0.5,
  });
  var t = Math.max(0, Math.floor(Number(g.tier) || 0));
  return {
    add: JUICE_TRAUMA.landingBase + JUICE_TRAUMA.landingPerTier * t,
    kick: downKick(JUICE_KICK.landingBase + JUICE_KICK.landingPerTier * t),
  };
}

/**
 * Shake booster: bubbles boil up from the jar floor, foam kicks out of its bottom corners.
 * @param {{ cx: number, bottom: number, halfW: number, mult: number, unit?: number }} g
 */
export function shakeBoosterFx(J, g) {
  var mult = g.mult > 0 ? g.mult : 1;
  J.sparkles(g.cx, g.bottom - g.halfW * 0.08, {
    count: Math.round(8 + 3 * mult), shape: "bubble", speed: [40, 120], size: [3.5 * (g.unit || 1), 8 * (g.unit || 1)],
    life: [0.7, 1.1], up: 150 * mult, angle: -Math.PI / 2, spread: Math.PI * 0.6, gravity: -140,
    radius: g.halfW * 0.85, drag: 1.4, delay: 0.12,
  });
  J.sparkles(g.cx, g.bottom - g.halfW * 0.08, {
    count: Math.round(6 + 3 * mult), colors: ["#ffffff", "#bfeaff"], shape: "dot", speed: [60, 160],
    size: [2.2, 4], life: [0.5, 0.8], up: 200 * mult, angle: -Math.PI / 2, spread: Math.PI * 0.7,
    gravity: 80, radius: g.halfW * 0.8,
  });
  // Foam kicks out of the bottom corners (sized to the layout, not the jar: a wide jar would
  // otherwise get cloud-sized foam that hides the creatures).
  var U = g.unit || 1;
  var foam = Math.min(g.halfW * 0.14, 20 * U);
  for (var side = -1; side <= 1; side += 2) {
    J.puff(g.cx + side * (g.halfW - foam * 1.2), g.bottom - foam * 0.8, {
      tone: "water", size: foam, vx: -side * 70 * U, vy: -50 * U, life: 0.45,
    });
    J.puff(g.cx + side * (g.halfW - foam * 2.6), g.bottom - foam * 0.5, {
      tone: "water", size: foam * 0.7, vx: -side * 50 * U, vy: -30 * U, life: 0.4, delay: 0.04,
    });
  }
  return { add: JUICE_TRAUMA.shakeBoosterPerMult * mult };
}

/** Clear booster: a cartoon poof on one removed body. @param {{ x: number, y: number, r: number, tier: number, i?: number }} g */
export function poofFx(J, g) {
  var key = tierPaletteKey(g.tier);
  var col = tierEntry(g.tier);
  var r = g.r;
  var d = (g.i || 0) * 0.012;
  J.flash(g.x, g.y, { palette: key, size: r * 1.1, life: 0.16, delay: d });
  J.puff(g.x, g.y, { tone: "smoke", size: r * 1.05, vy: -26, life: 0.5, delay: d });
  J.puff(g.x + r * 0.5, g.y - r * 0.3, { tone: "smoke", size: r * 0.6, vx: 30, vy: -30, life: 0.42, delay: 0.04 + d });
  J.puff(g.x - r * 0.5, g.y - r * 0.2, { tone: "smoke", size: r * 0.55, vx: -30, vy: -26, life: 0.42, delay: 0.05 + d });
  J.sparkles(g.x, g.y, { count: 4, colors: [col.accent, col.base], shape: "mix", speed: [60, 150], size: [2.2, 4], up: 60, gravity: 200 });
  return null;
}

/**
 * Shrink booster: the player watches the creature get smaller. Small violet puffs on a ring
 * just outside it are sucked inward with the sparkles while the 0.2 s shrink plays (nothing
 * sits on the creature itself), then a small poof rings the new, smaller size.
 * @param {{ x: number, y: number, r: number, newR?: number }} g r = old radius, newR = radius after
 */
export function shrinkFx(J, g) {
  var r = g.r;
  var nr = g.newR > 0 && g.newR < r ? g.newR : r * 0.72;
  var a0 = Math.random() * Math.PI * 2;
  for (var i = 0; i < 6; i++) {
    var a = a0 + (i / 6) * Math.PI * 2;
    var ca = Math.cos(a), sa = Math.sin(a);
    J.puff(g.x + ca * r * 1.12, g.y + sa * r * 1.12, {
      tone: "violet", size: r * rand(0.2, 0.26), vx: -ca * r * 2.6, vy: -sa * r * 2.6, life: 0.26,
    });
  }
  J.ring(g.x, g.y, { radius: r * 1.5, palette: "violet", life: 0.32, width: 1, start: 1 });
  J.sparkles(g.x, g.y, {
    count: 10, palette: "violet", shape: "mix", speed: [-160, -90], size: [2.4, 4.4], radius: r * 1.4,
    onRing: true, up: 0, gravity: 0, drag: 3,
  });
  // The poof once it has shrunk: a little ring of puffs blooming off the new rim.
  for (var k = 0; k < 4; k++) {
    var b = a0 + Math.PI / 4 + (k / 4) * Math.PI * 2;
    J.puff(g.x + Math.cos(b) * nr * 1.05, g.y + Math.sin(b) * nr * 1.05, {
      tone: "violet", size: nr * 0.34, vx: Math.cos(b) * nr * 2.2, vy: Math.sin(b) * nr * 2.2 - 10, life: 0.3, delay: 0.21,
    });
  }
  J.sparkles(g.x, g.y, {
    count: 6, palette: "violet", shape: "sparkle-ink", speed: [70, 140], size: [2.6, 4.2], radius: nr,
    onRing: true, up: 20, gravity: 120, drag: 2.4, delay: 0.21,
  });
  return null;
}

/** Heal: hearts float out of the jar mouth. @param {{ cx: number, top: number, halfW: number }} g */
export function healFx(J, g) {
  J.hearts(g.cx, g.top + g.halfW * 0.25, { count: 9, radius: g.halfW * 0.5, size: [6, 10] });
  J.ring(g.cx, g.top + g.halfW * 0.2, { radius: g.halfW * 0.9, palette: "heart", life: 0.4, squash: 0.4, width: 1.2 });
  J.sparkles(g.cx, g.top, { count: 8, palette: "heart", shape: "sparkle", speed: [60, 160], up: 80, gravity: 120, radius: g.halfW * 0.5 });
  return null;
}

/** Merge-stage revive: gold glow, hearts and sparkles in the jar. @param {{ cx: number, cy: number, halfW: number }} g */
export function jarReviveFx(J, g) {
  J.glow(g.cx, g.cy, { size: g.halfW * 3.2, color: "#ffe08a", life: 0.5, alpha: 0.5 });
  J.hearts(g.cx, g.cy, { count: 10, radius: g.halfW * 0.6, size: [6, 10] });
  J.sparkles(g.cx, g.cy, { count: 14, palette: "gold", shape: "mix", speed: [80, 220], radius: g.halfW * 0.6, up: 60, gravity: 150 });
  return null;
}

/**
 * Trail settings for a falling object by type (for J.trail each frame).
 * @param {string} type FallPhysics object type
 * @param {number} r collider radius (px)
 * @param {number} unit layout unit
 * @param {number} [tier]
 */
export function fallTrailFor(type, r, unit, tier) {
  // Boulders leave a rolling dust plume: puffs laid by distance (`spacing`, under half a puff's
  // width) overlap at any speed and frame rate, so it reads as one trail rather than a dotted
  // line of pebbles; `maxLength` keeps a fast plume about seven boulder radii long.
  if (type === "boulder") {
    return {
      kind: "puff", tone: "stone", every: 0.06, spacing: r * 0.6, maxLength: r * 7, size: r * 0.62, life: 0.36,
      jitter: r * 0.12, vy: -16, layer: "back",
    };
  }
  if (type === "boulder_debris") {
    return { kind: "puff", tone: "dust", every: 0.09, size: r * 0.6, life: 0.32, jitter: r * 0.2, vy: -10, layer: "back" };
  }
  // Creatures, gems and shields get cartoon speed lines from the Fall renderer instead
  // (run/fall-renderer.js SPEED_LINE_TYPES), so they leave no particle trail here.
  return null;
}
